import { useQuery } from "@tanstack/react-query";
import { useOktaAuth } from "@okta/okta-react";
import axios from "axios";

/**
 * Custom hook to fetch TPAR history data and calculate the maximum end date
 * @param {number} rpid - Parent company ID
 * @param {number} organizationId - Current organization ID to ensure cache invalidation on org change
 * @returns {object} - Object containing maxEndDate, isLoading, error, and data
 */
export function useFetchTparHistoryMaxDate(rpid, organizationId) {
    const { oktaAuth } = useOktaAuth();

    return useQuery({
        queryKey: ["tparHistoryMaxDate", rpid, organizationId],
        queryFn: async () => {
            const accessToken = oktaAuth.getAccessToken();
            const response = await axios.get("api/ThirdPartyReceivable/GetTPARHistoryData", {
                params: { rpid },
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response.data;
        },
        enabled: !!rpid,
        staleTime: 3 * 60 * 1000, // 3 minutes
        cacheTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
        select: (data) => {
            // Calculate the maximum end date from the history data
            if (!data || !Array.isArray(data) || data.length === 0) {
                return null;
            }

            const maxEndDate = new Date(Math.max(...data.map(item => new Date(item.endDate))));

            // Return the max end date as the start date (no additional day added)
            // Return timestamp to prevent new Date object references causing infinite loops
            return maxEndDate.getTime();
        }
    });
}
